import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { updateUser, deleteUser as apiDeleteUser } from '../services/userService';
import { getPledgesByUserId } from '../services/pledgeService';
import { getCharities } from '../services/dataService';
import { getCachedData } from '../services/cacheService';
import { sendPushNotification } from '../services/pushService';
import LoadingSpinner from './common/LoadingSpinner';
import ConfirmModal from './common/ConfirmModal';

const UserView = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [pledges, setPledges] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    is_admin: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState('');
  const [sendingNotification, setSendingNotification] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Load charities data to cache for name lookups
        await getCharities();
        
        const { user: userData, pledges: userPledges } = await getPledgesByUserId(userId);
        if (!userData) {
          setError('User not found.');
          setUser(null);
          setPledges([]);
        } else {
          setUser(userData);
          setPledges(userPledges);
          setFormData({
            name: userData.name || '',
            email: userData.email || '',
            is_admin: userData.is_admin || false
          });
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    if (userId) {
      loadData();
    }
  }, [userId]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value, 10) : value)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccessMessage('');
    try {
      // The backend rejects any payload containing wallet-era fields
      // (balance_cents). Send only the permitted user fields.
      const updatePayload = {
        name: formData.name,
        email: formData.email,
        is_admin: formData.is_admin,
      };
      await updateUser(userId, updatePayload);
      setUser({ ...user, ...updatePayload });
      setSuccessMessage('User details updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating user:', err);
      // Handle the case where update succeeds but backend returns 404 due to lookup issue
      if (err.message.includes('404') || err.message.includes('not found')) {
        const updatePayload = {
          name: formData.name,
          email: formData.email,
          is_admin: formData.is_admin,
        };
        setUser({ ...user, ...updatePayload });
        setSuccessMessage('User details updated successfully! (Please refresh to see server confirmation)');
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setError('Failed to update user. ' + (err.message || ''));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async () => {
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await apiDeleteUser(userId);
      console.log(`User ${userId} deleted.`);
      navigate('/'); // Navigate back to home or users list
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user.');
      setLoading(false);
    } finally {
      setShowConfirmModal(false);
    }
  };

  const predefinedMessages = [
    'Congratulations, you completed your mission this week!',
    'You have 1 more checkin to go... do it before Friday, keep going',
    'You checked in twice this week, well done.'
  ];

  const handleSendNotification = async () => {
    if (!selectedMessage.trim()) {
      alert('Please select a message to send.');
      return;
    }

    setSendingNotification(true);
    try {
      const response = await sendPushNotification(
        userId,
        'Fidareo Update',
        selectedMessage
      );
      
      alert(`Notification sent successfully to ${response.devices_sent} device(s)!`);
      setSelectedMessage('');
    } catch (err) {
      console.error('Error sending push notification:', err);
      alert(`Failed to send notification: ${err.message}`);
    } finally {
      setSendingNotification(false);
    }
  };

  // Helper function to lookup charity name from cache
  const getCharityNameById = (charityId) => {
    const cachedCharities = getCachedData('charities');
    if (cachedCharities && cachedCharities.data && cachedCharities.data.charities) {
      const charity = cachedCharities.data.charities.find(c => c.id === charityId);
      return charity ? charity.name : '';
    }
    return '';
  };

  if (loading) return <div className="flex justify-center items-center h-screen"><LoadingSpinner /></div>;
  if (error && !user) return <div className="container mx-auto p-4 text-red-500 text-center">{error}</div>;
  if (!user) return <div className="container mx-auto p-4 text-center">User not found.</div>;

  const pledgeColumns = [
    {
      key: 'charity_id',
      header: 'Charity Name',
      render: (charityId) => {
        const charityName = getCharityNameById(charityId);
        return charityName || '';
      }
    },
    { key: 'amount_cents', header: 'Amount', render: (val) => `$${(val / 100).toFixed(2)}` },
    { key: 'status', header: 'Status' },
    { key: 'start_date', header: 'Start Date', render: (val) => new Date(val).toLocaleDateString() },
    { key: 'end_date', header: 'End Date', render: (val) => new Date(val).toLocaleDateString() },
  ];

  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      <div className="bg-white shadow-xl rounded-lg p-6 md:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 pb-4 border-b border-gray-200">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">{user.name}</h1>
            <p className="text-gray-600 text-sm">{user.email}</p>
          </div>
          <button
            onClick={handleDeleteUser}
            disabled={loading}
            className="mt-4 sm:mt-0 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 ease-in-out disabled:opacity-50"
          >
            {loading ? 'Deleting...' : 'Delete User'}
          </button>
        </div>

        {error && <p className="text-red-500 bg-red-100 p-3 rounded-md mb-4">{error}</p>}
        {successMessage && <p className="text-green-500 bg-green-100 p-3 rounded-md mb-4">{successMessage}</p>}

        {/* User Info Display */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-1">User ID</h3>
            <p className="text-gray-600">{user.id}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-1">Admin Status</h3>
            <p className="text-gray-600">{user.is_admin ? 'Yes' : 'No'}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-1">Joined On</h3>
            <p className="text-gray-600">{new Date(user.created_at).toLocaleDateString()}</p>
          </div>
           <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-1">Auth Provider</h3>
            <p className="text-gray-600">{user.auth_provider || 'N/A'}</p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-1">Last Updated</h3>
            <p className="text-gray-600">{new Date(user.updated_at).toLocaleString()}</p>
          </div>
        </div>

        {/* Edit Form */}
        <form onSubmit={handleSubmit} className="mb-8 pt-6 border-t border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Edit User</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
              <input 
                type="text" 
                name="name" 
                id="name" 
                value={formData.name} 
                onChange={handleChange} 
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" 
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
              <input 
                type="email" 
                name="email" 
                id="email" 
                value={formData.email} 
                onChange={handleChange} 
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" 
              />
            </div>
            <div className="flex items-center">
              <input 
                type="checkbox" 
                name="is_admin" 
                id="is_admin" 
                checked={formData.is_admin} 
                onChange={handleChange} 
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" 
              />
              <label htmlFor="is_admin" className="ml-2 block text-sm text-gray-900">Admin Status</label>
            </div>
          </div>
          <div className="mt-8 flex justify-end">
            <button
              type="submit"
              disabled={saving || loading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-lg transition duration-150 ease-in-out disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        {/* Push Notification Section */}
        <div className="mb-8 pt-6 border-t border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Send Push Notification</h2>
          <div className="bg-gray-50 p-6 rounded-lg">
            <div className="mb-4">
              <label htmlFor="message-select" className="block text-sm font-medium text-gray-700 mb-2">
                Select Message
              </label>
              <select
                id="message-select"
                value={selectedMessage}
                onChange={(e) => setSelectedMessage(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                disabled={sendingNotification}
              >
                <option value="">-- Select a message --</option>
                {predefinedMessages.map((message, index) => (
                  <option key={index} value={message}>
                    {message}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleSendNotification}
                disabled={sendingNotification || !selectedMessage.trim()}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendingNotification ? 'Sending...' : 'Send Notification'}
              </button>
            </div>
          </div>
        </div>
        
        <h2 className="text-2xl font-semibold text-gray-800 mb-4 mt-8 pt-4 border-t border-gray-200">Pledges by User</h2>
        {pledges.length > 0 ? (
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 bg-white">
              <thead className="bg-gray-50">
                <tr>
                  {pledgeColumns.map(col => (
                    <th key={col.key} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {col.header}
                    </th>
                  ))}
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pledges.map(pledge => (
                  <tr key={pledge.id} className="hover:bg-gray-50 transition-colors">
                    {pledgeColumns.map(col => (
                      <td key={col.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {col.render ? col.render(pledge[col.key], pledge) : (pledge[col.key] === undefined || pledge[col.key] === null ? 'N/A' : String(pledge[col.key]))}
                      </td>
                    ))}
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Link to={`/pledges/${pledge.id}`} className="text-indigo-600 hover:text-indigo-900">View Pledge</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-600">This user has not made any pledges.</p>
        )}
      </div>

      <button
          onClick={() => navigate(-1)}
          className="mt-8 bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
        >
          Back
      </button>

      {showConfirmModal && (
        <ConfirmModal
          isOpen={showConfirmModal}
          title="Confirm User Deletion"
          message={`Are you sure you want to delete user "${user.name}" (ID: ${user.id})? This action cannot be undone.`}
          onConfirm={confirmDelete}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
    </div>
  );
};

export default UserView;
